import LazyVideo from "@/components/LazyVideo";
import React, { useEffect, useState } from 'react';
import { Seo } from '../components/Seo';
import { clinicInfo, providerRef } from '../data/clinicInfo';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { categoryMedia } from '../data/content';
import { useContent } from '@/context/ContentContext';
import {
  ArrowLeft, ArrowUpLeft, Calendar, Plus, Minus,
  Activity, Sprout, Leaf, Wind, Droplets, Sparkles, ShieldCheck,
} from 'lucide-react';
import { Appointment } from '../components/Appointment';

const BENEFIT_ICONS = [Activity, Sprout, Leaf, Wind, Droplets, Sparkles, ShieldCheck];

const FAQItem: React.FC<{ q: string; a: string; index: number }> = ({ q, a, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-5% 0px' }}
      transition={{ duration: 0.45, delay: index * 0.05 }}
      className="border-b border-[#412018]/15 last:border-b-0"
    >
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-6 py-7 md:py-8 text-right group/q"
      >
        <span className="font-serif text-[#412018] text-[2.2rem] md:text-[2.4rem] lg:text-[3rem] leading-[1.15] flex-1 group-hover/q:text-[#67392b] transition-colors">
          {q}
        </span>
        <span className="w-10 h-10 rounded-full border border-[#412018]/25 flex items-center justify-center text-[#67392b] shrink-0 group-hover/q:bg-[#67392b] group-hover/q:text-[#EAE6D7] transition-colors duration-300">
          {open ? <Minus size={16} strokeWidth={1.6} /> : <Plus size={16} strokeWidth={1.6} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <p className="text-[#412018]/75 font-light text-[16px] md:text-[17px] leading-[1.85] pb-8 max-w-[55ch]">
              {a}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export const ServiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { services: servicesData } = useContent();
  const service = servicesData.find((s) => s.id === id);

  const [showStickyBar, setShowStickyBar] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowStickyBar(window.scrollY > window.innerHeight * 0.7);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!service) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-[#EAE6D7] text-[#412018]" dir="rtl">
        <div className="text-center">
          <h1 className="text-4xl font-serif mb-4">השירות לא נמצא</h1>
          <Link to="/services" className="text-[#67392b] hover:underline inline-flex items-center gap-2">
            <ArrowLeft size={16} /> חזרה לשירותים
          </Link>
        </div>
      </div>
    );
  }

  const relatedServices = servicesData
    .filter((s) => s.id !== service.id && s.category === service.category)
    .slice(0, 2);
  const upsells =
    relatedServices.length >= 2
      ? relatedServices
      : servicesData.filter((s) => s.id !== service.id).slice(0, 2);

  


  // Split title for italic accent: last word in #67392b italic
  const words = service.title.trim().split(' ');
  const titleHead = words.slice(0, -1).join(' ');
  const titleTail = words[words.length - 1];

  const displayPrice = service.price || 'מחיר אישי';
  const displayDuration = service.duration || '75 דק׳';
  const isNumericPrice = /\d/.test(displayPrice);

  const sections: { label: string; en: string; body: string }[] = [
    { label: 'למי מתאים', en: 'Who it suits', body: service.suitableFor },
    { label: 'לפני הטיפול', en: 'Before', body: service.beforeTreatment },
    { label: 'אחרי הטיפול', en: 'After', body: service.afterTreatment },
  ];

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: service.title,
    description: service.description,
    serviceType: service.category,
    provider: providerRef,
    areaServed: 'IL',
    url: `${clinicInfo.url}/services/${service.id}`,
    ...(isNumericPrice ? { offers: { '@type': 'Offer', price: displayPrice.replace(/\D/g, ''), priceCurrency: clinicInfo.currency, url: `${clinicInfo.url}/services/${service.id}` } } : {}),
  };
  const faqJsonLd = service.faq && service.faq.length
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: service.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      }
    : null;

  return (
    <>
      <Seo
        title={`${service.title} | RIHAM`}
        description={service.description.slice(0, 155)}
        path={`/services/${service.id}`}
        jsonLd={faqJsonLd ? [serviceJsonLd, faqJsonLd] : serviceJsonLd}
      />
    <div className="bg-[#EAE6D7] min-h-[100dvh] pt-32 text-[#412018] relative" dir="rtl">
      <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

      {/* HERO — full-bleed media, headline overlaid, fade into page */}
      {(() => {
        const heroMedia = categoryMedia[service.category] || { media: service.media, mediaType: service.mediaType };
        return (
      <section className="relative w-full min-h-[620px] h-[88vh] max-h-[920px] overflow-hidden -mt-32 mb-12 md:mb-20">
        {heroMedia.mediaType === 'video' ? (
          <LazyVideo src={heroMedia.media} autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover"  aria-hidden="true" />
        ) : (
          <img src={heroMedia.media} alt={service.title} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-[hsl(47_31%_88%/0.72)]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[hsl(47_31%_88%/0.45)] via-transparent to-[hsl(47_31%_88%/0.55)]" />
        <div
          className="absolute -bottom-px left-0 right-0 h-[60%] pointer-events-none"
          style={{
            background:
              'linear-gradient(to top, hsl(47 31% 88%) 0%, hsl(47 31% 88% / 0.98) 18%, hsl(47 31% 88% / 0.85) 38%, hsl(47 31% 88% / 0.5) 62%, hsl(47 31% 88% / 0) 100%)',
          }}
        />
        <div className="absolute inset-0 opacity-40 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-multiply" />

        <div className="relative h-full flex flex-col items-center justify-center text-center px-6 pt-28 pb-24 md:pb-32">
          {/* Breadcrumb */}
          <motion.nav
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="flex items-center gap-3 text-[10px] uppercase tracking-[0.35em] text-[#412018]/60 font-bold mb-8"
          >
            <Link to="/" className="hover:text-[#67392b] transition-colors">ראשי</Link>
            <span className="w-4 h-px bg-[#412018]/30" />
            <Link to="/services" className="hover:text-[#67392b] transition-colors">טיפולים</Link>
          </motion.nav>

          <motion.span
            initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-6"
          >
            {service.subtitle}
          </motion.span>

          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-serif text-[#412018] leading-[0.95] tracking-wide max-w-5xl text-[3.4rem] sm:text-6xl md:text-7xl lg:text-[6rem]"
          >
            {titleHead && <>{titleHead} </>}
            <span className="text-[#67392b] italic">{titleTail}</span>
          </motion.h1>

          {(service as any).tagline && (
            <motion.p
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.3 }}
              className="mt-8 max-w-2xl text-[#412018]/75 font-light text-base md:text-lg leading-relaxed"
            >
              {(service as any).tagline}
            </motion.p>
          )}

          {/* Subtle scroll cue */}
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.8 }}
            className="mt-14 md:mt-20 flex flex-col items-center gap-3 text-[#412018]/50"
          >
            <span className="text-[9px] uppercase tracking-[0.4em] font-bold">גלילה</span>
            <span className="w-px h-10 bg-[#412018]/30" />
          </motion.div>
        </div>
      </section>
        );
      })()}

      {/* ESSENCE — centered big heading + paragraph description */}
      <section className="relative py-16 md:py-32">
        <div className="container mx-auto px-5 md:px-6 max-w-[1100px] relative">
          <div className="text-center max-w-[840px] mx-auto">
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
              Essence
            </p>
            <h2 className="font-serif leading-[0.92] tracking-wide text-[3.6rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] mb-6 md:mb-8">
              <span className="text-[#412018]">מהות </span>
              <span className="text-[#67392b] italic">הטיפול</span>
            </h2>
            <div className="w-12 h-px bg-[#67392b] mx-auto mb-8 md:mb-10" />

            {(service as any).paragraphs && (service as any).paragraphs.length > 0 ? (
              <div className="space-y-7 text-right" dir="rtl">
                {((service as any).paragraphs as string[]).slice(0, 1).map((p, i) => (
                  <motion.p
                    key={i}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-5% 0px' }}
                    transition={{ duration: 0.6, delay: i * 0.08 }}
                    className="text-[#412018]/80 font-light text-[15.5px] md:text-[19px] leading-[1.8] md:leading-[1.85]"
                  >
                    {p}
                  </motion.p>
                ))}
              </div>
            ) : (service as any).tagline && (
              <p className="text-[#412018]/80 font-light text-base md:text-xl leading-[1.85]">
                {(service as any).tagline}
              </p>
            )}
          </div>
        </div>
      </section>

      {/* PROTOCOL */}
      {sections.some((s) => s.body) && (
        <section className="bg-[#412018] text-[#EAE6D7] relative overflow-hidden py-16 md:py-32">
          <div className="absolute inset-0 opacity-15 bg-[url('https://www.transparenttextures.com/patterns/cream-paper.png')] pointer-events-none mix-blend-overlay" />
          <div className="container mx-auto px-5 md:px-6 max-w-[1200px] relative">
            <div className="text-center max-w-[820px] mx-auto mb-12 md:mb-20">
              <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#fbbf25] font-bold mb-5 md:mb-7">
                Protocol
              </p>
              <h2 className="font-serif leading-[0.92] tracking-wide text-[3.6rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] mb-6 md:mb-8">
                <span className="text-[#EAE6D7]">פרוטוקול </span>
                <span className="text-[#fbbf25] italic">הטיפול</span>
              </h2>
              <div className="w-12 h-px bg-[#fbbf25] mx-auto mb-6 md:mb-8" />
              <p className="text-[#EAE6D7]/70 font-light text-[15px] md:text-lg leading-relaxed">
                לפני, במהלך ואחרי — שלושה צעדים פשוטים שמבטיחים חוויה רציפה ותוצאה מיטבית.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#EAE6D7]/15 border border-[#EAE6D7]/15">
              {sections.filter((s) => s.body).map((sec, idx) => (
                <motion.div
                  key={sec.label}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: '-5% 0px' }}
                  transition={{ duration: 0.5, delay: idx * 0.08 }}
                  className="bg-[#412018] p-6 md:p-10 flex flex-col gap-4 md:gap-5 min-h-[200px] md:min-h-[260px]"
                >
                  <span className="text-[10px] uppercase tracking-[0.35em] text-[#fbbf25]/80 font-bold">
                    {sec.en}
                  </span>
                  <h3 className="font-serif leading-[1] tracking-wide text-[#EAE6D7] text-[2.2rem] md:text-[2.6rem] lg:text-[3rem]">
                    {sec.label}
                  </h3>
                  <div className="w-10 h-px bg-[#fbbf25]/60" />
                  <p className="text-[#EAE6D7]/75 font-light leading-relaxed text-[14.5px] md:text-[15px] mt-auto">
                    {sec.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {(service as any).faq && (service as any).faq.length > 0 && (
        <section className="relative py-16 md:py-32">
          <div className="container mx-auto px-5 md:px-6 max-w-[1000px] relative">
            <div className="text-center max-w-[820px] mx-auto mb-12 md:mb-20">
              <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
                FAQ
              </p>
              <h2 className="font-serif leading-[0.92] tracking-wide text-[3.6rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] mb-6 md:mb-8">
                <span className="text-[#412018]">שאלות </span>
                <span className="text-[#67392b] italic">נפוצות</span>
              </h2>
              <div className="w-12 h-px bg-[#67392b] mx-auto mb-6 md:mb-8" />
              <p className="text-[#412018]/70 font-light text-[15px] md:text-lg leading-relaxed">
                כל מה שחשוב לדעת לפני הטיפול — ואם נשארה שאלה פתוחה, תמיד אפשר לפנות אלינו.
              </p>
            </div>

            <div className="border-y border-[#412018]/15">
              {((service as any).faq as { q: string; a: string }[]).map((item, i) => (
                <FAQItem key={i} q={item.q} a={item.a} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      <div className="container mx-auto px-5 md:px-6 relative z-10 max-w-[1280px]">

        {/* BUNDLE — typographic editorial, no media */}
        <section className="mt-20 md:mt-32 mb-20 md:mb-32">
          <div className="text-center max-w-[820px] mx-auto mb-12 md:mb-20">
            <p className="text-[10px] md:text-[11px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-7">
              Holistic Bundle
            </p>
            <h2 className="font-serif leading-[0.92] tracking-wide text-[3.6rem] sm:text-6xl md:text-7xl lg:text-[6.5rem] mb-6 md:mb-8">
              <span className="text-[#412018]">חוויה </span>
              <span className="text-[#67392b] italic">שלמה</span>
            </h2>
            <div className="w-12 h-px bg-[#67392b] mx-auto mb-6 md:mb-8" />
            <p className="text-[#412018]/75 font-light text-[15px] md:text-lg leading-relaxed">
              שלבי את הטיפול עם טיפולים משלימים לתוצאות מקסימליות — וקבלי 15% הנחה על כל החבילה.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 lg:gap-16 items-stretch border-y border-[#412018]/15">
            {/* List of bundle items */}
            <div className="lg:col-span-7 lg:border-l lg:border-[#412018]/15 lg:pl-16 py-6 lg:py-14">
              <ul className="divide-y divide-[#412018]/12">
                {[service, upsells[0], upsells[1]].map((item, i) => (
                  <motion.li
                    key={item.id}
                    initial={{ opacity: 0, y: 14 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-5% 0px' }}
                    transition={{ duration: 0.5, delay: i * 0.07 }}
                    className="py-5 md:py-7 flex items-baseline gap-4 md:gap-6"
                  >
                    <span className="font-sans tabular-nums text-[10px] md:text-[11px] text-[#67392b]/60 tracking-[0.3em] w-6 md:w-8 shrink-0">
                      0{i + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-[#67392b] font-bold mb-1.5 md:mb-2">
                        {item.subtitle}
                      </p>
                      {i === 0 ? (
                        <h3 className="font-serif text-[#412018] text-[2.2rem] md:text-[2.6rem] lg:text-[3rem] leading-[1.05]">
                          {item.title}
                        </h3>
                      ) : (
                        <Link to={`/services/${item.id}`} className="block group/i">
                          <h3 className="font-serif text-[#412018] text-[2.2rem] md:text-[2.6rem] lg:text-[3rem] leading-[1.05] group-hover/i:text-[#67392b] transition-colors">
                            {item.title}
                          </h3>
                        </Link>
                      )}
                    </div>
                    <span className="font-sans font-light text-[#412018]/70 text-sm md:text-base tabular-nums shrink-0 self-center">
                      {item.price || 'מחיר אישי'}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {/* Price + CTA */}
            <div className="lg:col-span-5 py-8 lg:py-14 border-t border-[#412018]/15 lg:border-t-0 flex flex-col justify-center">
              <p className="text-[10px] uppercase tracking-[0.4em] text-[#67392b] font-bold mb-5 md:mb-6">
                חבילה משולבת
              </p>
              <p className="font-serif text-[#412018] text-3xl md:text-4xl lg:text-[3.2rem] leading-none mb-7 md:mb-8">
                3 טיפולים ביחד — 15% הנחה
              </p>
              <button
                onClick={() => document.getElementById('appointment')?.scrollIntoView({ behavior: 'smooth' })}
                className="group/cta relative overflow-hidden bg-[#412018] text-[#fbbf25] px-6 md:px-7 py-3.5 md:py-4 inline-flex self-start"
              >
                <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover/cta:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.7,0,0.2,1)]" />
                <span className="relative flex items-center justify-center gap-3 group-hover/cta:text-[#412018] transition-colors duration-500">
                  <Calendar size={14} strokeWidth={1.6} />
                  <span className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.3em]">קבעי תור לחבילה</span>
                </span>
              </button>
            </div>

          </div>
        </section>

      </div>

      <Appointment />

      {/* STICKY BOTTOM BAR — appears on scroll */}
      <AnimatePresence>
        {showStickyBar && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-0 left-0 right-0 z-40 bg-[#EAE6D7]/95 backdrop-blur-md border-t border-[#412018]/15"
            dir="rtl"
          >
            <div className="container mx-auto px-4 md:px-6 max-w-[1400px]">
              <div className="flex items-center gap-3 md:gap-8 py-3 md:py-5">
                {/* Title — hidden on small */}
                <div className="hidden md:block flex-1 min-w-0">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-[#67392b] font-bold mb-1.5">
                    {service.subtitle}
                  </p>
                  <p className="font-serif text-[#412018] text-2xl lg:text-[1.9rem] xl:text-[2.2rem] leading-[1.05] truncate">
                    {service.title}
                  </p>
                </div>

                {/* Duration */}
                <div className="flex flex-col items-start shrink-0">
                  <span className="text-[9px] uppercase tracking-[0.25em] text-[#412018]/55">משך</span>
                  <span className="font-sans font-light text-[#412018] text-[1rem] tabular-nums leading-tight">
                    {displayDuration}
                  </span>
                </div>

                <span className="w-px h-8 bg-[#412018]/20 shrink-0" />

                {/* Price */}
                <div className="flex flex-col items-start shrink-0">
                  <span className="text-[9px] uppercase tracking-[0.25em] text-[#412018]/55">מחיר</span>
                  <span className={`font-sans font-light text-[#412018] tabular-nums leading-tight ${isNumericPrice ? 'text-[1rem]' : 'text-[0.85rem]'}`}>
                    {displayPrice}
                  </span>
                </div>

                {/* CTA */}
                <button
                  onClick={() => document.getElementById('appointment')?.scrollIntoView({ behavior: 'smooth' })}
                  className="group/cta relative overflow-hidden bg-[#412018] text-[#fbbf25] px-4 md:px-6 py-3 mr-auto shrink-0"
                >
                  <span className="absolute inset-0 bg-[#fbbf25] translate-y-full group-hover/cta:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.7,0,0.2,1)]" />
                  <span className="relative flex items-center justify-center gap-2 group-hover/cta:text-[#412018] transition-colors duration-500">
                    <Calendar size={12} strokeWidth={1.6} />
                    <span className="text-[10px] font-bold uppercase tracking-[0.28em]">לקביעת תור</span>
                  </span>
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
    </>
  );
};
